import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import { axiosPrivate } from '@/api/axios';
import { USER_API_PATH } from '@/lib/constants';
import { useOnboardingProgress } from './hooks/useOnboardingProgress';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const TUTORIALS = [
  {
    id: 'video1',
    title: 'Configura tu cuenta',
    embedUrl: 'https://www.youtube.com/embed/fXvrc5o7G7c',
    description: 'Aprende a configurar tu cuenta para aprovechar al máximo la plataforma',
  },
  {
    id: 'video2',
    title: 'Crea y envía una cotización',
    embedUrl: 'https://www.youtube.com/embed/Ko5C_PQDf_k',
    description: 'Guía paso a paso para crear y enviar cotizaciones',
  },
  {
    id: 'video3',
    title: 'Mantén informados a tus clientes',
    embedUrl: 'https://www.youtube.com/embed/LexLPaXcU-o',
    description: 'Aprende a mantener comunicación efectiva con tus clientes',
  },
  {
    id: 'video4',
    title: 'Incrementa tu ticket promedio',
    embedUrl: 'https://www.youtube.com/embed/HdTfjlLslQs',
    description: 'Estrategias para aumentar tus ventas',
  },
  {
    id: 'video5',
    title: 'Automatiza tu postventa',
    embedUrl: 'https://www.youtube.com/embed/IPMmYEkIn1M',
    description: 'Automatiza procesos de seguimiento',
  },
  {
    id: 'video6',
    title: 'Carga tu inventario',
    embedUrl: 'https://www.youtube.com/embed/O-cTp4Wzbn4',
    description: 'Gestiona tu inventario de forma eficiente',
  },
  {
    id: 'video7',
    title: 'Conoce tus deudores',
    embedUrl: 'https://www.youtube.com/embed/PD_IbzDiiio',
    description: 'Mantén control de las cuentas por cobrar',
  },
];

export default function Welcome() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { progress, markVideoCompleted, markOnboardingCompleted } = useOnboardingProgress();
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [showDismissDialog, setShowDismissDialog] = useState(false);

  const isChild = currentUser ? currentUser.companyId !== currentUser.id : false;

  const waUrl = useMemo(() => {
    if (!currentUser) return '';
    const base = `https://wa.me/5215576444990?text=Hola,%20deseo%20activar%20mi%20cuenta%20con%20id:%20${currentUser.id}`;
    return currentUser.companyId && currentUser.companyId !== currentUser.id
      ? `${base}%20y%20id%20de%20empresa%20${currentUser.companyId}`
      : base;
  }, [currentUser]);

  const overallProgress = useMemo(() => {
    const watchedCount = progress.completedVideos.length;
    const verificationPoints = currentUser?.emailVerified ? 1 : 0;
    return Math.round(
      ((watchedCount / TUTORIALS.length) * 0.7 + verificationPoints * 0.3) * 100
    );
  }, [progress.completedVideos, currentUser?.emailVerified]);

  const handleVideoClick = async (videoId: string) => {
    setPlayingVideo(videoId);
    await markVideoCompleted(videoId, TUTORIALS.length);
  };

  const handleDismiss = async () => {
    await markOnboardingCompleted();

    try {
      const config = currentUser?.config || {};
      config.dismissedWelcome = true;
      await axiosPrivate.patch(`/${USER_API_PATH}/${currentUser?.id}`, { config });
    } catch (err) {
      console.error('[Welcome] dismiss error:', err);
    }

    navigate('/shop/reports/eod', { replace: true });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* WhatsApp verification banner */}
        {!isChild && !currentUser?.emailVerified && (
          <Card variant="legacy" className="mb-6 p-4 bg-amber-50 border-amber-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-amber-800">Verifica tu cuenta por WhatsApp</h3>
                <p className="text-sm text-amber-700">
                  Envía tu ID para activar tu cuenta completamente.
                </p>
              </div>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Abrir WhatsApp
              </a>
            </div>
          </Card>
        )}

        {/* Progress */}
        <Card variant="legacy" className="mb-6 p-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-bold text-slate-700">Progreso de onboarding</h2>
            <span className="text-sm text-slate-500">{overallProgress}%</span>
          </div>
          <Progress value={overallProgress} className="h-3" />
          <p className="text-xs text-slate-400 mt-2">
            {progress.completedVideos.length}/{TUTORIALS.length} videos completados
            {currentUser?.emailVerified ? ' • Email verificado' : ' • Email pendiente de verificar'}
          </p>
        </Card>

        {/* Tutorial videos grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {TUTORIALS.map(tutorial => {
            const isCompleted = progress.completedVideos.includes(tutorial.id);
            const isPlaying = playingVideo === tutorial.id;

            return (
              <Card key={tutorial.id} variant="legacy" className="overflow-hidden">
                {isPlaying ? (
                  <div className="aspect-video">
                    <iframe
                      src={`${tutorial.embedUrl}?autoplay=1`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={tutorial.title}
                    />
                  </div>
                ) : (
                  <button
                    onClick={() => handleVideoClick(tutorial.id)}
                    className="w-full aspect-video bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors relative"
                  >
                    <svg className="h-12 w-12 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    {isCompleted && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                        Visto
                      </div>
                    )}
                  </button>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                    {tutorial.title}
                    {isCompleted && (
                      <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </h3>
                  <p className="text-sm text-slate-500 mt-1">{tutorial.description}</p>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Dismiss button */}
        <div className="text-center">
          <Button
            onClick={() => setShowDismissDialog(true)}
            disabled={!currentUser?.emailVerified}
            variant={currentUser?.emailVerified ? 'default' : 'outline'}
          >
            {currentUser?.emailVerified ? 'Ir al dashboard' : 'Verifica tu email para continuar'}
          </Button>
        </div>

        {/* Dismiss confirmation dialog */}
        <AlertDialog open={showDismissDialog} onOpenChange={setShowDismissDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Todo listo?</AlertDialogTitle>
              <AlertDialogDescription>
                No volveremos a mostrar esta pantalla de bienvenida. Siempre podrás acceder a los tutoriales desde la sección de ayuda.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDismiss}>
                Todo listo
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
